import React, { useEffect, useState, useContext } from 'react';
import proptypes from 'prop-types';
import { QueryProvider, useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { FitCompatibilityProvider, FitCompatibilityWidget } from './components';
import { FitDrawer, IconInfoDrawer } from './components/subcomponents';
import { REFRIGERATOR_ANIMATIONS, REFRIGERATOR_IMAGES } from './applianceConstants/refrigerator';
import { LAUNDRY_ANIMATIONS, LAUNDRY_IMAGES } from './applianceConstants/laundry';
import { RANGE_IMAGES, RANGE_ANIMATIONS } from './applianceConstants/range';
import { OTR_MICROWAVES_ANIMATIONS } from './applianceConstants/otr-microwaves';
import { DISHWASHER_ANIMATIONS } from './applianceConstants/dishwasher';
 
import {
  TYPE_LIST,
  VALID_APPLIANCE_TYPES
} from './constants';
import { isBuiltInAppliance, getApplianceType } from './utils';
 
const { oneOf, bool, string, shape } = proptypes;
 
export const FitCompatibility = ({
  navParam = '',
  itemId = '',
  type = '',
  drawerPosition = 'right',
  imageCacheEnabled = false,
  data: searchPropData = undefined,
  enableOptionsThatFitDrawer = false,
  noPipSpacing = false
}) => {
  const [isLoading, setIsLoading] = useState(imageCacheEnabled && typeof window !== 'undefined');
  const [isValidApplianceType, setIsValidApplianceType] = useState(false);
  const isSearchPropValid = typeof searchPropData !== 'undefined';
 
  const experienceContext = useContext(ExperienceContext);
  const { customContext } = experienceContext || {};
  const { variables } = customContext || {};
  const contextNavParam = variables?.navParam;
  const effectiveNavParam = contextNavParam || navParam;
 
  const {
    data: productData,
    loading: productLoading,
    error: productError
  } = useDataModel('product', {
    variables: {
      itemId
    },
    skip: type !== 'PIP'
  });
 
  const {
    data: searchData,
    loading: searchLoading,
    error: searchError
  } = useDataModel('searchModel', {
    variables: variables || { navParam: effectiveNavParam },
    ssr: true,
    skip: type === 'PIP' || isSearchPropValid || !effectiveNavParam
  });
 
  let activeData;
 
  if (type === 'PIP') {
    activeData = productData;
  } else if (isSearchPropValid) {
    activeData = searchPropData;
  } else {
    activeData = searchData;
  }
 
  const applianceType = getApplianceType(activeData) || '';
  const hideWidgetFitForBuiltIn = type === 'PIP' && isBuiltInAppliance(activeData);
 
  useEffect(() => {
    if (!productLoading && !searchLoading && activeData) {
      const valid = VALID_APPLIANCE_TYPES.includes(applianceType);
      setIsValidApplianceType(valid);
    }
  }, [activeData]);
 
  /* istanbul ignore next */
  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = src;
      image.onload = resolve;
      image.onerror = reject;
    });
  };
 
  /* istanbul ignore next */
  useEffect(() => {
    const cacheImages = async () => {
      if (typeof window === 'undefined') return;
 
      try {
        if (!TYPE_LIST.includes(type)) throw new Error('No type provided to FitCompatibility');
        await Promise.all(
          [
            ...Object.values(REFRIGERATOR_ANIMATIONS),
            ...Object.values(REFRIGERATOR_IMAGES),
            ...Object.values(LAUNDRY_ANIMATIONS),
            ...Object.values(LAUNDRY_IMAGES),
            ...Object.values(RANGE_ANIMATIONS),
            ...Object.values(RANGE_IMAGES),
            ...Object.values(OTR_MICROWAVES_ANIMATIONS),
            ...Object.values(DISHWASHER_ANIMATIONS)
          ].map(loadImage)
        );
        setIsLoading(false);
      } catch (er) {
        console.log(er);
        setIsLoading(false);
      }
    };
    if (imageCacheEnabled) cacheImages();
  }, [imageCacheEnabled, type]);
 
  const shouldHide =
    !TYPE_LIST.includes(type) ||
    !isValidApplianceType ||
    hideWidgetFitForBuiltIn;
 
  if (shouldHide) {
    return null;
  }
 
  return (
    <div
      data-testid="fit-compatibility"
      data-component="FitCompatibility"
    >
      <QueryProvider cacheKey="fit-compatibility">
        <FitCompatibilityProvider
          isLoading={isLoading}
          type={type}
          itemId={itemId}
          navParam={effectiveNavParam}
          drawerPosition={drawerPosition}
          applianceType={applianceType}
          productData={activeData}
          enableOptionsThatFitDrawer={enableOptionsThatFitDrawer}
          noPipSpacing={noPipSpacing}
        >
          <FitDrawer />
          <IconInfoDrawer />
          <FitCompatibilityWidget />
        </FitCompatibilityProvider>
      </QueryProvider>
    </div>
  );
};
 
FitCompatibility.propTypes = {
  type: oneOf(TYPE_LIST),
  itemId: string,
  navParam: string,
  drawerPosition: oneOf(['left', 'right']),
  imageCacheEnabled: bool,
  data: shape({}),
  enableOptionsThatFitDrawer: bool,
  noPipSpacing: bool
};
 
FitCompatibility.dataModel = FitCompatibilityProvider.dataModel;
 
FitCompatibility.displayName = 'FitCompatibility';